/**
 * MPACK kiekio blokai (1c etapas, blocks/mpack-qty-tiers.liquid).
 *
 * Modulis nieko neskaičiuoja ir nieko nededa į krepšelį. Kainas nupiešė
 * Liquid iš temos nustatymų, o dėjimą daro Vessel'io produkto forma. Čia
 * lieka vienas darbas: laikyti sinchroniškus tris dalykus, kurie kalba apie
 * tą patį kiekį — pažymėtą bloką, `input[name="quantity"]` formoje ir lipnios
 * juostos paslėptą kiekį.
 *
 * Kodėl ne per `change` įvykį ant laukelio: Vessel'io kiekio laukelis klauso
 * `on:blur="/setQuantity"`, o ne `change`, tad sintetinis `change` nueitų į
 * niekur. Todėl kviečiamas pats komponento metodas (jis pritraukia prie
 * `min`/`step`/likučio ir paskelbia `quantity-selector:update`), o jei jo
 * kada nors nebeliktų, reikšmė vis tiek lieka įrašyta į laukelį ir forma
 * pasiųs teisingą kiekį.
 *
 * Atvirkštinė kryptis irgi yra: paspaudus „+/−" arba įvedus skaičių ranka,
 * bloko žymė nusiima, kai kiekis nebeatitinka nė vienos pakopos. Pažymėtas
 * „6 paketai" prie dešimties krepšelyje būtų melas apie pasirinkimą.
 */

class MpackQtyTiers extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    const { signal } = this.#abort;

    this.radios = Array.from(this.querySelectorAll('.mpack-qty__radio'));
    if (this.radios.length === 0) return;

    this.addEventListener('change', this.#onPick, { signal });
    document.addEventListener('quantity-selector:update', this.#onQuantity, { signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /** Pagrindinės produkto formos kiekio laukelis (ne krepšelio eilutės). */
  get #quantityInput() {
    const root = this.closest('.shopify-section') ?? document;
    return root.querySelector('product-form-component input[name="quantity"]');
  }

  /** Lipnios juostos forma (sections/mpack-sticky-cta), jei ji šiame puslapyje yra. */
  get #stickyInput() {
    return document.querySelector('mpack-sticky-cta form input[name="quantity"]');
  }

  #onPick = (event) => {
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement) || radio.type !== 'radio') return;

    const quantity = Number.parseInt(radio.value, 10);
    if (!Number.isFinite(quantity) || quantity < 1) return;

    const input = this.#quantityInput;
    if (!input) return;

    input.value = String(quantity);

    const selector = input.closest('quantity-selector-component');
    if (selector && typeof selector.setQuantity === 'function') {
      // Metodas tikisi įvykio: jam reikia tik `target` ir `preventDefault`.
      selector.setQuantity({ target: input, preventDefault() {} });
    }

    this.#mirror(quantity);
  };

  #onQuantity = (event) => {
    // Krepšelio eilutės kiekis keliauja tuo pačiu įvykiu, bet su `cartLine`.
    if (event.detail?.cartLine) return;
    if (!(event.target instanceof HTMLElement) || !event.target.closest('product-form-component')) return;

    const quantity = Number(event.detail?.quantity);
    for (const radio of this.radios) {
      radio.checked = Number(radio.value) === quantity;
    }
    this.#mirror(quantity);
  };

  /**
   * Lipni juosta turi savo formą su paslėptu kiekiu (ji negali remtis
   * pagrindine, nes tos id priklauso nuo šablono). Be šios eilutės pirkėjas,
   * pasirinkęs šešis paketus ir nuslinkęs žemyn, juostos mygtuku gautų vieną.
   */
  #mirror(quantity) {
    const sticky = this.#stickyInput;
    if (sticky && Number.isFinite(quantity) && quantity > 0) {
      sticky.value = String(quantity);
    }
  }
}

if (!customElements.get('mpack-qty-tiers')) {
  customElements.define('mpack-qty-tiers', MpackQtyTiers);
}
